import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { cachedGet, fileUrl } from "@/lib/api";

const IMG = "https://images.unsplash.com/photo-1721623777765-1381ba32859c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBjbGFzc2ljYWwlMjBtdXNpYyUyMHBlcmZvcm1hbmNlJTIwZGFyayUyMHN0YWdlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg2MDAwMDU2fDA&ixlib=rb-4.1.0&q=75&w=1000&auto=format";

const CHAPTERS = [
  { n: "01", title: "The Call", text: "Sumiran — the act of remembering the divine. What began as a living-room satsang became a calling: to carry bhajans beyond the temple hall and onto the grand stage." },
  { n: "02", title: "The Sound", text: "Harmonium and tabla at the heart, guitar, flute and bass around it. Ancient verses set to arrangements that swell like film scores — reverent, yet cinematic." },
  { n: "03", title: "The Stage", text: "From Mumbai to Jaipur, our ticketed sandhyas draw thousands into one voice. Every show ends the same way — the whole hall singing together, strangers no longer." },
];

export default function About() {
  const ref = useRef(null);
  const [aboutImg, setAboutImg] = useState(() => localStorage.getItem("sumiran_about_img") || IMG);
  const [title, setTitle] = useState(() => localStorage.getItem("sumiran_about_title") || "Devotion, performed");
  const [italic, setItalic] = useState(() => localStorage.getItem("sumiran_about_italic") || "without walls");
  const [chapters, setChapters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sumiran_about_chapters")) || CHAPTERS; } catch { return CHAPTERS; }
  });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  useEffect(() => {
    cachedGet("/settings").then(({ data }) => {
      const imgUrl = data.about_image_url ? fileUrl(data.about_image_url) : IMG;
      const t = data.about_title || "Devotion, performed";
      const it = data.about_italic || "without walls";
      const custom = [1, 2, 3].map((i) => ({
        n: `0${i}`,
        title: data[`about_ch${i}_title`],
        text: data[`about_ch${i}_text`],
      }));
      const chs = custom.every((c) => c.title && c.text) ? custom : CHAPTERS;
      localStorage.setItem("sumiran_about_img", imgUrl);
      localStorage.setItem("sumiran_about_title", t);
      localStorage.setItem("sumiran_about_italic", it);
      localStorage.setItem("sumiran_about_chapters", JSON.stringify(chs));
      setAboutImg(imgUrl);
      setTitle(t);
      setItalic(it);
      setChapters(chs);
    }).catch(() => {});
  }, []);

  return (
    <section id="about" ref={ref} className="relative px-6 md:px-12 py-24 md:py-32" data-testid="about-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 relative order-2 lg:order-1">
          <div className="absolute -inset-3 border border-saffron/30 translate-x-4 translate-y-4" aria-hidden="true" />
          <motion.div className="relative overflow-hidden" style={{ y }}>
            <img src={aboutImg} alt="The Band Sumiran lead vocalist performing live" loading="lazy" decoding="async" className="w-full aspect-[4/5] object-cover" data-testid="about-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070605]/70 via-transparent to-transparent" />
          </motion.div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeading overline="The Band" title={title} italic={italic} testid="about-heading" />
          <div className="mt-14 space-y-12">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-6 md:gap-10 items-start border-t border-bone/10 pt-8">
                  <span className="font-display text-saffron text-sm tracking-[0.3em] pt-1">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl sm:text-3xl text-bone">{c.title}</h3>
                    <p className="mt-3 text-base lg:text-lg leading-relaxed text-bone/70 max-w-xl">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
